import React, { useEffect } from 'react';
import { motion, Variants } from 'framer-motion';
import { User, Users, ChevronRight } from 'lucide-react';
import { useDashboardStore } from '../../store/dashboardStore';

interface SettingsPanelProps {
  isMobile?: boolean;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ isMobile = false }) => {
  const {
    setShowStaffListPanel,
    setShowDatePanel,
    setShowDetailPanel,
    setShowAllPatientListPanel,
    setShowPatientFormPanel, // 患者情報パネルを閉じるための関数
    setShowStaffFormPanel, // スタッフフォームを閉じるための関数
    setShowSettingsPanel, // 設定パネルを閉じるための関数
    openAllPatientList, // 設定から患者一覧を開くための関数
    isNavigatingBack,
    setIsNavigatingBack,
    skipFlowSwitchAnimation,
    setSkipFlowSwitchAnimation,
    settingsReturningToRoot,
    settingsRootRevealFromWorkflow,
  } = useDashboardStore();

  const skipEnterAnimation = isMobile && skipFlowSwitchAnimation;
  const instantSettingsRoot =
    isMobile && (settingsReturningToRoot || settingsRootRevealFromWorkflow);
  const settingsBehindExitingOverlay = isMobile && settingsRootRevealFromWorkflow;

  useEffect(() => {
    if (isMobile && skipFlowSwitchAnimation) {
      setSkipFlowSwitchAnimation(false);
    }
  }, [isMobile, skipFlowSwitchAnimation, setSkipFlowSwitchAnimation]);

  // スタッフ一覧がクリックされた時の処理
  const handleStaffListClick = () => {
    // 進む動作なので、isNavigatingBackをfalseにリセット
    setIsNavigatingBack(false);
    setShowDatePanel(false);
    setShowDetailPanel(false);
    setShowAllPatientListPanel(false);
    setShowPatientFormPanel(false);
    setShowStaffFormPanel(false); // スタッフフォームを閉じる
    // スマホ版では、設定パネルを閉じる
    if (isMobile) {
      setShowSettingsPanel(false);
    }
    setShowStaffListPanel(true);
  };

  // 患者一覧がクリックされた時の処理
  const handlePatientListClick = () => {
    // 進む動作なので、isNavigatingBackをfalseにリセット
    setIsNavigatingBack(false);
    // スマホ版では、設定パネルを閉じる
    if (isMobile) {
      setShowSettingsPanel(false);
    }
    // 設定から患者一覧を開くための専用関数を使用
    openAllPatientList();
  };

  const panelVariants: Variants = isMobile 
    ? {
        hidden: { x: '100%', opacity: 1 },
        hiddenBack: { x: '-100%', opacity: 1 },
        visible: { x: 0, opacity: 1 },
      }
    : {
        hidden: { x: '-100%' },  // デスクトップ版は左から
        visible: { x: 0 },
      };

  const menuItems: {
    title: string;
    subtitle: string;
    Icon: typeof Users;
    onClick: () => void;
  }[] = [
    { title: '患者一覧', subtitle: '追加・編集', Icon: Users, onClick: handlePatientListClick },
    { title: 'スタッフ一覧', subtitle: '追加・編集・予約登録', Icon: User, onClick: handleStaffListClick },
  ];

  return (
    <motion.div
      variants={panelVariants}
      initial={
        skipEnterAnimation || instantSettingsRoot
          ? 'visible'
          : isMobile && isNavigatingBack
            ? 'hiddenBack'
            : 'hidden'
      }
      animate="visible"
      exit={
        isMobile
          ? isNavigatingBack
            ? { x: '100%', opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }
            : { x: '-100%' }
          : undefined
      }
      transition={
        skipEnterAnimation || instantSettingsRoot ? { duration: 0 } : { duration: 0.3, ease: 'easeOut' }
      }
      className={
        isMobile
          ? `fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] bg-white flex flex-col shadow-lg ${
              settingsBehindExitingOverlay ? 'z-[38]' : 'z-40'
            }`
          : 'panel-col panel-col--settings z-50 bg-white border-r flex flex-col shadow-lg'
      }
    >
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-800">設定パネル</h2>
        </div>
      </div>
      <div className={`flex-1 overflow-y-auto ${isMobile ? 'pb-[var(--bottom-menu-total)]' : ''}`}>
        <div className="p-2 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.title}
              type="button"
              onClick={item.onClick}
              className="w-full rounded-lg border border-gray-200 bg-white p-3 text-left flex items-center justify-between gap-3 transition-colors hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <item.Icon size={20} className="text-gray-600 shrink-0" />
                <div className="flex min-w-0 flex-col items-start gap-0.5">
                  <span className="text-base font-medium text-gray-900">{item.title}</span>
                  <span className="text-xs text-gray-500">{item.subtitle}</span>
                </div>
              </div>
              <ChevronRight size={16} className="text-gray-400 shrink-0" aria-hidden />
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};
